import { Group, MultiSelect, MultiSelectProps } from '@mantine/core';
import { type QueryObserverResult } from '@tanstack/react-query';
import { isArray, uniq } from 'lodash';
import React, { useMemo, useState } from 'react';
import { LabelNode } from '../../../../../../react-commons/components/inputs/getLabelNode';
import { getColorMantine } from '../../../../../../react-commons/components/inputs/text-fields/types';
import { ValidationEndAdornment } from '../../../../../../react-commons/components/ValidationEndAdornment';
import { formatIdentifier } from '../../../../../../react-commons/utils/utilFunctions';
import { type PersistableResource } from '../../../../resource/resource-base/persistableResourceTypes';
import { type PropertyDetailsResource } from '../../../../resource/resources/propertyDetailsResource';
import { type PropertyValidation } from '../../../../resource/services/outlineTypes';
import { compareAlphanumerically, getPropertyLabelInfo } from '../../../util/utilFunctions';
import { ControlledInputs } from '../../../../../../react-commons/utils/read-only-inputs/controlledInputs';
import { Except, SetRequired } from 'type-fest';

type Controlled_MultiSelectReferenceInputProps = Except<
    SetRequired<MultiSelectProps, 'readOnly' | 'value' | 'data'>,
    | 'nothingFoundMessage'
    | 'limit'
    | 'searchable'
    | 'clearable'
    | 'rightSectionPointerEvents'
    | 'maxDropdownHeight'
    | 'comboboxProps'
>;

const Controlled_MultiSelectReferenceInput = (
    props: Controlled_MultiSelectReferenceInputProps
) => {
    return (
        <MultiSelect
            {...props}
            nothingFoundMessage="No options"
            limit={10}
            searchable={true}
            clearable={!props.readOnly}
            rightSectionPointerEvents={'all'}
            maxDropdownHeight={200}
            comboboxProps={{ zIndex: 9000 }}
        />
    );
};



type PersistableWithDescription = PersistableResource & {
    description: string;
};

// TODO T extends PersistableResource, P extends T
export const Controlled_AutocompleteMultiSelectReferenceTextField = <P extends PersistableResource>({
    idValues,
    propertyDetails,
    propertyValidation,
    query,
    label,
    getOptionLabel,
    groupBy,
    onChange,
    filterResources,
    readOnly,
    disabled,
    idSelector = 'objectId',
    hideLabel = false,
}: {
    idValues: string[] | undefined;
    label?: string;
    propertyDetails: PropertyDetailsResource | PropertyDetailsResource[] | undefined;
    propertyValidation: PropertyValidation | null | undefined;
    getOptionLabel: (resource: P) => string;
    filterResources?: (resources: P[]) => P[];
    readOnly?: boolean;
    query: QueryObserverResult<P | P[] | { content: P } | { content: P[] } | null>;
    groupBy?: (resource: P) => string;
    getToolTip?: (resource: P | undefined) => React.ReactElement | undefined;
    onChange?: (value: string[] | null) => void;
    disabled?: boolean;
    onCloseButtonClick?: () => void;
    idSelector?: 'id' | 'objectId';
    customPlaceHolder?: string;
    internalLabelWhenNotSet?: string | React.ReactNode;
    hideLabel?: boolean;
}) => {
    const severity = propertyValidation?.severity;
    const helperText = propertyValidation?.identifier;

    const [isFocused, setIsFocused] = useState(false)

    const queryData = useMemo(
        () =>
            query.data?.let((it) => {
                if ('content' in it) {
                    return it.content as P | P[];
                } else {
                    return it;
                }
            }),
        [query.data],
    );

    const optionsArray = useMemo(() => {
        const array =
            Array.isArray(queryData) ? queryData
                : queryData ? [queryData]
                    : [];

        array.toSorted((a, b) => compareAlphanumerically(getOptionLabel(a), getOptionLabel(b)));
        if (groupBy) {
            array.toSorted((a, b) => groupBy(a).localeCompare(groupBy(b)));
        }
        if (filterResources) {
            return filterResources(array);
        }
        return array;
    }, [queryData, filterResources]);


    const data = useMemo(() => {
        const getResourceValue = (resource: P) => {
            if (idSelector === 'objectId' && 'objectId' in resource) {
                return (resource.objectId as string) ?? '';
            } else {
                return resource.id ?? '';
            }
        };

        const getResourceDescription = (resource: P) => {
            const description = (resource as PersistableWithDescription).description;
            return Boolean(description) ? description : undefined;
        };

        if (groupBy) {
            const individuallyGrouped = optionsArray.map((value) => ({
                group: groupBy(value),
                item: {
                    value: getResourceValue(value),
                    label: getOptionLabel(value),
                    description: getResourceDescription(value),
                },
            }));
            const groupsFound = uniq(individuallyGrouped.map((value) => value.group));
            return groupsFound.map((groupValue) => ({
                group: groupValue,
                items: individuallyGrouped.filter((value) => value.group === groupValue).map((value) => value.item).toSorted((a, b) => a.label.localeCompare(b.label)),
            })).toSorted((a, b) => a.group.localeCompare(b.group));
        }
        return optionsArray.map((value) => ({
            value: getResourceValue(value),
            label: getOptionLabel(value),
            description: getResourceDescription(value),
        })).toSorted((a, b) => a.label.localeCompare(b.label));
    }, [optionsArray, idSelector]);

    const getRightSectionWidth = () => {
        let currentWidth = 29.5;
        if (severity && helperText) {
            currentWidth = currentWidth + 29.5;
        }
        return currentWidth;
    };

    const autoCompleteContent = isFocused ? (
        <Controlled_MultiSelectReferenceInput
            autoFocus
            value={isFocused ? idValues ?? [] : []}
            onChange={onChange}
            onBlur={() => setIsFocused(false)}
            placeholder={!idValues || !idValues.length ? 'nothing selected' : undefined}
            label={
                !hideLabel && (
                    <LabelNode
                        labelInfo={getPropertyLabelInfo(propertyDetails)}
                        label={
                            isArray(propertyDetails) ?
                                propertyDetails[0]?.displayName ?? formatIdentifier(propertyDetails[0]?.propertyName)
                                : propertyDetails ?
                                    propertyDetails?.displayName ?? formatIdentifier(propertyDetails.propertyName)
                                    : label
                        }
                    />
                )
            }
            readOnly={readOnly ?? false}
            data={data}
            disabled={disabled}
            size={'sm'}
            styles={{
                root: {
                    pointerEvents: disabled ? 'none' : undefined,
                    width: '100%',
                },
                input: {
                    borderColor: getColorMantine(severity),
                    backgroundColor: readOnly ? 'var(--mantine-color-read-only-input)' : undefined,
                },
            }}
            rightSectionWidth={getRightSectionWidth()}
            rightSection={
                <Group
                    justify={'flex-end'}
                    gap={4}
                    style={{
                        paddingRight: 4,
                        alignItems: 'center',
                    }}
                    w={'100%'}
                >
                    <ValidationEndAdornment severity={severity} helperText={helperText ? [helperText] : undefined} />
                </Group>
            }
        />
    ): (
        <ControlledInputs.String
            onFocus={() => setIsFocused(true)}
            nullable={false}
            value={idValues?.length ? `${idValues?.length} selected` : ''}
            placeholder={"nothing selected"}
            readOnly={readOnly ?? false}
        />
    );

    return (
        <div style={{ display: 'inline-flex', width: '100%', minWidth: 112 }}>
            {autoCompleteContent}
        </div>
    );
};
